import React, { useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import Button from "../button/button";

// Every capability added since the basic recorder, grouped so visitors can scan by need.
const featureGroups = [
    {
        id: "ai",
        label: "AI Tools",
        icon: "mdi:auto-fix",
        features: [
            {
                icon: "mdi:text-to-speech",
                title: "Text to Speech",
                image: "/img/aiTools/tts.png",
                description:
                    "Upload a text file and hear it read back in a natural voice, up to 5,000 characters at a time.",
            },
            {
                icon: "mdi:text-box-outline",
                title: "AI Summary",
                image: "/img/aiTools/summary.png",
                description:
                    "Get the key points of a long recording written out for you, so you never replay an hour to find one detail.",
            },
            {
                icon: "mdi:translate",
                title: "Translate Text",
                image: "/img/aiTools/translate.png",
                description:
                    "Upload a text file and turn it into any of more than 45 languages, then copy the translation or download it.",
            },
            {
                icon: "mdi:apps",
                title: "One AI Tools Hub",
                image: "/img/aiTools/hub.svg",
                description:
                    "Every tool sits on a single screen in your dashboard. Pick a file, pick a tool, and the result lands in My Recordings.",
            },
        ],
    },
    {
        id: "transcription",
        label: "Transcription",
        icon: "mdi:text-recognition",
        features: [
            {
                icon: "mdi:text-recognition",
                title: "Automatic Transcription",
                image: "/img/aiTools/transcribe.png",
                description:
                    "Turn recordings of up to 30 minutes into a full written transcript, complete with a summary alongside it.",
            },
            {
                icon: "mdi:file-document-edit-outline",
                title: "Edit, Copy & Download",
                image: "/img/aiTools/edit-download.svg",
                description:
                    "Correct a word, copy the text straight to your clipboard, or download the transcript and summary as a .txt file.",
            },
            {
                icon: "mdi:translate-variant",
                title: "Translate The Transcript",
                image: "/img/aiTools/translate-transcript.svg",
                description:
                    "Read a transcript or its summary in another language without leaving the screen you transcribed it on.",
            },
            {
                icon: "mdi:file-music-outline",
                title: "Transcribe Your Own Files",
                image: "/img/aiTools/transcribe-own.svg",
                description:
                    "Audio recorded somewhere else works too. Upload it and transcribe it in the same place as everything you record here.",
            },
        ],
    },
    {
        id: "audio",
        label: "Audio Editing",
        icon: "mdi:waveform",
        features: [
            {
                icon: "mdi:tune-vertical-variant",
                title: "Audio Adjuster",
                image: "/img/aiTools/adjuster.png",
                description:
                    "Remove background noise, boost quiet audio and change playback speed anywhere from 0.5x to 2x.",
            },
            {
                icon: "mdi:content-cut",
                title: "Trim Audio",
                image: "/img/aiTools/trim.png",
                description:
                    "Cut a recording down on the waveform and keep only the part that matters, no editing experience needed.",
            },
            {
                icon: "mdi:movie-open-outline",
                title: "Extract Audio From Video",
                image: "/img/aiTools/extract-audio.png",
                description:
                    "Upload an MP4, MOV or WebM video and pull the audio straight out of it into your recordings.",
            },
            {
                icon: "mdi:call-merge",
                title: "Merge Recordings",
                image: "/img/aiTools/merge.svg",
                description:
                    "Join several clips into one seamless file, made for podcasters, interviewers and content creators.",
            },
            {
                icon: "mdi:file-swap-outline",
                title: "Convert Formats",
                image: "/img/aiTools/convert.svg",
                description:
                    "Save your audio as MP3, WAV, M4A or CAF so it plays on every device you need it to.",
            },
        ],
    },
    {
        id: "productivity",
        label: "Productivity",
        icon: "mdi:lightning-bolt-outline",
        features: [
            {
                icon: "mdi:note-edit-outline",
                title: "Notes On Recordings",
                image: "/img/aiTools/note.png",
                description:
                    "Attach a note to any recording while it is still fresh, then edit or remove it whenever you like.",
            },
            {
                icon: "mdi:folder-multiple-outline",
                title: "Folders",
                image: "/img/aiTools/folders.svg",
                description:
                    "Group recordings into folders so a growing library stays easy to move around in.",
            },
            {
                icon: "mdi:star-outline",
                title: "Favourites",
                image: "/img/aiTools/favourites.svg",
                description:
                    "Star the recordings you keep coming back to and pull them up without scrolling the whole list.",
            },
            {
                icon: "mdi:cloud-sync-outline",
                title: "Cloud Sync",
                image: "/img/aiTools/cloud-sync.svg",
                description:
                    "Your recordings sync to the cloud, so they are waiting on every device you sign in on.",
            },
        ],
    },
];

const AiFeaturesSection = ({ onGetStarted }) => {
    const [activeTab, setActiveTab] = useState(featureGroups[0].id);

    const visibleGroups = featureGroups.filter((group) => group.id === activeTab);

    return (
        <div id="ai-features-section" style={{ padding: "60px 0", background: "#F8FAFC" }}>
            <div className="container">
                <div className="text-center" style={{ marginBottom: "32px" }}>
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            background: "#E2E8F0",
                            color: "#334155",
                            borderRadius: "20px",
                            padding: "6px 14px",
                            fontFamily: "Metropolis",
                            fontSize: "12px",
                            fontWeight: "700",
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                            marginBottom: "16px",
                        }}
                    >
                        <Icon icon="mdi:star-four-points-outline" style={{ fontSize: "14px" }} />
                        New in Voice Recorder
                    </span>
                    <h2
                        style={{
                            fontFamily: "Metropolis",
                            fontWeight: "700",
                            fontSize: "40px",
                            letterSpacing: "-1px",
                            marginBottom: "12px",
                        }}
                    >
                        More Than A Recorder: AI, Transcripts & Editing
                    </h2>
                    <p
                        style={{
                            fontFamily: "Metropolis",
                            fontSize: "18px",
                            color: "#5F626B",
                            maxWidth: "680px",
                            margin: "0 auto",
                        }}
                    >
                        Record it, read it, edit it and find it again. Everything below is built into your dashboard,
                        with no extra software to install.
                    </p>
                </div>

                {/* Tabs let visitors jump to the group they came for without losing the rest. */}
                <div
                    className="d-flex flex-wrap justify-content-center"
                    style={{ gap: "10px", marginBottom: "36px" }}
                >
                    {featureGroups.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                aria-pressed={isActive}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    background: isActive ? "#334155" : "#FFFFFF",
                                    color: isActive ? "#FFFFFF" : "#5F626B",
                                    border: `1px solid ${isActive ? "#334155" : "#E2E8F0"}`,
                                    borderRadius: "30px",
                                    padding: "10px 20px",
                                    fontFamily: "Metropolis",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                }}
                            >
                                <Icon icon={tab.icon} style={{ fontSize: "18px" }} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {visibleGroups.map((group) => (
                    <div key={group.id} style={{ marginBottom: "36px" }}>
                        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                            {group.features.map((feature) => (
                                <div key={feature.title} className="col">
                                    <div
                                        style={{
                                            background: "#FFFFFF",
                                            border: "1px solid #F1F5F9",
                                            borderRadius: "14px",
                                            padding: "24px",
                                            height: "100%",
                                            boxShadow: "0px 4px 20px rgba(148, 163, 184, 0.12)",
                                        }}
                                    >
                                        {feature.image ? (
                                            <img
                                                src={feature.image}
                                                alt={feature.title}
                                                width={42}
                                                height={42}
                                                style={{ borderRadius: "12px", marginBottom: "16px" }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    background: "#334155",
                                                    borderRadius: "12px",
                                                    width: "42px",
                                                    height: "42px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    marginBottom: "16px",
                                                }}
                                            >
                                                <Icon icon={feature.icon} style={{ color: "#FFFFFF", fontSize: "22px" }} />
                                            </div>
                                        )}
                                        <p
                                            style={{
                                                fontFamily: "Metropolis",
                                                fontWeight: "700",
                                                fontSize: "18px",
                                                color: "#334155",
                                                marginBottom: "6px",
                                            }}
                                        >
                                            {feature.title}
                                        </p>
                                        <p
                                            style={{
                                                fontFamily: "Metropolis",
                                                color: "#5F626B",
                                                fontSize: "15px",
                                                lineHeight: "22px",
                                                marginBottom: "0",
                                            }}
                                        >
                                            {feature.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}

                <div className="d-flex justify-content-center">
                    <Button btnText={"Try The AI Tools"} primary arrow onClick={onGetStarted} />
                </div>
            </div>
        </div>
    );
};

export default AiFeaturesSection;
